import { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Header } from './components/Layout/Header';
import { Sidebar } from './components/Layout/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';

const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));
const HelpSupportPage = lazy(() => import('./pages/HelpSupport/HelpSupportPage'));
const AdminVerification = lazy(() => import('./pages/BusinessPortal/AdminVerification'));
const SecurityOpsPage = lazy(() => import('./pages/SecurityOps/SecurityOpsPage'));
const SystemHealthPage = lazy(() => import('./pages/SecurityOps/SystemHealthPage'));
const ActivityLogsPage = lazy(() => import('./pages/SecurityOps/ActivityLogsPage'));
const BusinessPortalAdmin = lazy(() => import('./pages/BusinessPortal/BusinessPortalAdmin'));
const Businesses = lazy(() => import('./pages/BusinessPortal/Businesses'));
const ReportDetail = lazy(() => import('./pages/HelpSupport/ReportDetail'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminUserPosts = lazy(() => import('./pages/AdminUserPosts'));
const EmailsPage = lazy(() => import('./pages/email/EmailsPage'));
const AdminOrders = lazy(() => import('./pages/Orders/AdminOrders'));
const CreateBlog = lazy(() => import('./pages/CreateBlog'));
const InsightsList = lazy(() => import('./pages/InsightsList'));

const AppContent = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const { loading, error, isAdmin } = useAuth();
  const isLoginRoute = location.pathname === '/admin/login' || location.pathname === '/login';

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  useEffect(() => {
  const handleEscape = (e) => {
    if (e.key === 'Escape') setIsSidebarOpen(false);
  };

  if (isSidebarOpen) {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', handleEscape);
      }
    };
  }
}, [isSidebarOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-500 animate-pulse">Loading app...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900">App Error</h2>
          <p className="text-gray-600">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Reload
          </button>
        </div>
      </div>
    );
  }
  if (isLoginRoute) {
    return (
      <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-lg text-gray-500 animate-pulse">Loading...</div>
          </div>
        }>
          <Routes>
            <Route path="/admin/login" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-gray-100">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
         <div className="flex-1 flex flex-col overflow-hidden">
          {location.pathname !== '/admin/security-ops/activity-logs' && (
            <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
          )}
          <main className={`flex-1 overflow-y-auto ${location.pathname === '/admin/security-ops/activity-logs' ? '' : ''}`}>
            <Suspense fallback={
              <div className="p-6 text-center text-gray-500 animate-pulse">
                Loading page...
              </div>
            }>
              <Routes>
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <AdminProtectedRoute>
                      <AdminDashboard />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/waitlist" 
                  element={
                    <ProtectedRoute>
                      <WaitlistPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
  path="/business-portal" 
  element={
    <AdminProtectedRoute>
      <BusinessPortalAdmin />
    </AdminProtectedRoute>
  } 
/>
              <Route
                path="/business-portal/verification"
                element={
                  <AdminProtectedRoute>
                    <AdminVerification />
                  </AdminProtectedRoute>
                }
              />
                               <Route
                   path="/business-portal/businesses"
                   element={
                     <AdminProtectedRoute>
                       <Businesses />
                     </AdminProtectedRoute>
                   }
                 />
<Route 
                  path="/" 
                  element={
                    <Navigate 
                      to={localStorage.getItem('token') ? "/admin/dashboard" : "/admin/login"} 
                      replace 
                    />
                  } 
                />
                <Route 
  path="/admin/notifications" 
  element={
    <AdminProtectedRoute>
      <NotificationsPage />
    </AdminProtectedRoute>
  } 
/>
<Route 
  path="/admin/help-support" 
  element={
    <AdminProtectedRoute>
      <HelpSupportPage />
    </AdminProtectedRoute>
  } 
/>
<Route 
  path="/admin/help-support/:reportId" 
  element={
    <AdminProtectedRoute>
      <ReportDetail />
    </AdminProtectedRoute>
  } 
/>
             <Route
               path="/admin/security-ops"
               element={
                 <AdminProtectedRoute>
                   <SecurityOpsPage />
                 </AdminProtectedRoute>
               }
             />
             <Route
               path="/admin/security-ops/system-health"
               element={
                 <AdminProtectedRoute>
                   <SystemHealthPage />
                 </AdminProtectedRoute>
               }
             />
             <Route
               path="/admin/security-ops/activity-logs"
               element={
                 <AdminProtectedRoute>
                   <ActivityLogsPage />
                 </AdminProtectedRoute>
               }
             />
<Route
  path="/admin/users"
  element={
    <AdminProtectedRoute>
      <Suspense fallback={<div className="p-8 text-center text-gray-600">Loading users list...</div>}>
        <AdminUsers />
      </Suspense>
    </AdminProtectedRoute>
  }
/>
<Route
  path="/admin/users/:id"
  element={
    <AdminProtectedRoute>
      <Suspense fallback={<div className="p-8 text-center text-gray-600">Loading user details...</div>}>
        <AdminUserPosts />
      </Suspense>
    </AdminProtectedRoute>
  }
/>
<Route
  path="/admin/emails"
  element={
    <AdminProtectedRoute>
      <EmailsPage />
    </AdminProtectedRoute>
  }
/>
<Route
                  path="/admin/orders"
                  element={
                    <AdminProtectedRoute>
                      <AdminOrders />
                    </AdminProtectedRoute>
                  }
                />
             <Route
  path="/admin/insights/create"
  element={
    <AdminProtectedRoute>
      <CreateBlog />
    </AdminProtectedRoute>
  }
/>
 <Route
  path="/admin/insights/edit/:id"
  element={
    <AdminProtectedRoute>
      <CreateBlog />
    </AdminProtectedRoute>
  }
/>
<Route
                  path="/admin/insights"
                  element={
                    <AdminProtectedRoute>
                      <InsightsList />
                    </AdminProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
};

const AdminProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-6">Verifying admin access...</div>;
  return (user && user.role === 'admin') ? children : <Navigate to="/admin/login" replace />;
};

export const App = () => (
  <AuthProvider>
    <Router>
      <AppContent />
    </Router>
  </AuthProvider>
);